import { PageHeader } from "@/components/page-header";
import { useDatasets } from "@/features/datasets/queries";
import { durationMs, useJobs } from "@/features/jobs/queries";
import { useModels } from "@/features/models/queries";
import { useCurrentProject } from "@/features/projects/current-project";
import { useRecipes } from "@/features/recipes/queries";
import { Link } from "@tanstack/react-router";

function Card({
  label,
  value,
  sub,
  href,
}: {
  label: string;
  value: React.ReactNode;
  sub?: string;
  href?: string;
}) {
  const inner = (
    <div className="bg-surface p-5 transition-colors hover:bg-surface-2">
      <div className="text-[10px] font-medium uppercase tracking-wider text-fg-muted">{label}</div>
      <div className="mt-2 text-[28px] font-normal leading-none tracking-tight tnum">{value}</div>
      {sub && <div className="mt-1.5 text-[11px] text-fg-muted mono">{sub}</div>}
    </div>
  );
  return href ? (
    <Link to={href} className="block">
      {inner}
    </Link>
  ) : (
    inner
  );
}

export function OverviewPage() {
  const { projectId, projectName } = useCurrentProject();
  const models = useModels(projectId);
  const jobs = useJobs(projectId);
  const datasets = useDatasets(projectId);
  const recipes = useRecipes(projectId);

  const modelItems = models.data?.items ?? [];
  const datasetItems = datasets.data?.items ?? [];
  const jobItems = jobs.data?.items ?? [];

  // Top-line totals come from data.total — accurate beyond the first page.
  // Detail breakdowns (active revs, ready datasets, running jobs) operate on
  // the current page slice; they are display-only hints, not authoritative
  // counts. The user lands on the feature page for accurate breakdowns.
  const totalModels = models.data?.total ?? 0;
  const totalDatasets = datasets.data?.total ?? 0;
  const totalRecipes = recipes.data?.total ?? 0;
  const activeRevs = modelItems.filter((m) => m.active_revision_id != null).length;
  const readyDatasets = datasetItems.filter((d) => d.status === "ready").length;
  const runningJobs = jobItems.filter(
    (j) => j.status === "running" || j.status === "queued",
  ).length;

  // Recent finished training jobs
  const recentDone = jobItems
    .filter((j) => j.status === "done" || j.status === "failed")
    .slice()
    .sort((a, b) => (b.finished_at ?? "").localeCompare(a.finished_at ?? ""))
    .slice(0, 5);

  if (!projectId) {
    return (
      <>
        <PageHeader title="Overview" subtitle="Project summary" />
        <div className="rounded-lg border border-border bg-surface p-8 text-center text-sm text-fg-muted">
          Select a project from the sidebar to see its summary.
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Overview" subtitle={projectName ?? "Project summary"} />
      <div className="mb-8 grid grid-cols-4 gap-px overflow-hidden rounded-lg border border-border bg-border">
        <Card
          label="Models"
          value={totalModels}
          sub={`${activeRevs} with active revision`}
          href="/models"
        />
        <Card
          label="Datasets"
          value={totalDatasets}
          sub={`${readyDatasets} ready for training`}
          href="/datasets"
        />
        <Card label="Recipes" value={totalRecipes} href="/recipes" />
        <Card
          label="Active jobs"
          value={runningJobs}
          sub={runningJobs > 0 ? "in progress" : "idle"}
          href="/jobs"
        />
      </div>

      <section className="rounded-lg border border-border bg-surface">
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <h2 className="text-xs font-medium">Recent jobs</h2>
          <Link to="/jobs" className="text-xs text-fg-muted hover:text-fg">
            View all →
          </Link>
        </div>
        {recentDone.length === 0 ? (
          <div className="p-6 text-center text-xs text-fg-muted">No jobs yet.</div>
        ) : (
          <ul className="divide-y divide-border">
            {recentDone.map((j) => {
              const ms = durationMs(j);
              const secs = ms != null ? Math.max(1, Math.floor(ms / 1000)) : null;
              return (
                <li key={j.id} className="flex items-center gap-3 px-4 py-2.5 text-xs">
                  <span
                    className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                      j.status === "done" ? "bg-success" : "bg-danger"
                    }`}
                  />
                  <span className="w-32 truncate mono text-fg-muted">{j.kind}</span>
                  <span className="flex-1 truncate text-fg-muted">
                    {j.target_kind ? `${j.target_kind} · ${j.target_id?.slice(0, 8)}…` : "—"}
                  </span>
                  <span className="mono tnum text-fg-muted">{secs != null ? `${secs}s` : "—"}</span>
                  <span className="mono text-fg-dim">
                    {j.finished_at ? new Date(j.finished_at).toLocaleString() : "—"}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </>
  );
}
